import { TableCell, TableRow } from "@mui/material";
import React, { useCallback, useEffect, useState } from "react";
import MultiSelect from "react-select";
import { SignatureFacilityUser, UserAssignedFacilityRow } from "./types";
import useLang from "Shared/hooks/useLanguage";
import { styles } from "Utils/Common";

export const reactSelectStyle = {
  control: (base: any) => ({
    ...base,
    boxShadow: "none",
    height: "auto !important",
    minHeight: "33px !important",
    borderRadius: ".475rem !important",
    padding: "0 0.4rem",
  }),
  menuPortal: (base: any) => ({ ...base, zIndex: 9999 }),
};

function resolveFacilityId(row: UserAssignedFacilityRow): string {
  const raw = row.facilityId ?? row.FacilityId;
  return raw !== null ? String(raw) : "";
}

type Option = { label: string; value: string };

const AssignedFacilityRow = (props: {
  row: UserAssignedFacilityRow;
  onFacilityUserSelectionChange: (
    facilityId: string,
    userIds: string[]
  ) => void;
}) => {
  const { row, onFacilityUserSelectionChange } = props;
  const facilityId = resolveFacilityId(row);
  const [users, setUsers] = useState<SignatureFacilityUser[]>(
    row.facilityUsers || []
  );
  const [selectedUsers, setSelectedUsers] = useState<Option[]>(() =>
    (row.facilityUsers || [])
      .filter((user) => user.isSelected)
      .map((user) => ({
        label: user.userName || "No Name",
        value: user.userId,
      }))
  );

  const formattedUsers: Option[] = users.map((user) => ({
    label: user.userName || "No Name",
    value: user.userId,
  }));

  const allSelected = users.length > 0 && selectedUsers.length === users.length;
  const { t } = useLang();

  const handleSelectAllUsers = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!facilityId) return;
    if (event.target.checked) {
      setSelectedUsers(formattedUsers);
      onFacilityUserSelectionChange(
        facilityId,
        formattedUsers.map((user) => user.value)
      );
    } else {
      setSelectedUsers([]);
      onFacilityUserSelectionChange(facilityId, []);
    }
  };

  const handleMultiSelectChange = useCallback(
    (selectedOptions: Option[] | null) => {
      if (!facilityId) return;
      const next = selectedOptions ?? [];
      setSelectedUsers([...next]);
      onFacilityUserSelectionChange(
        facilityId,
        next.map((option) => option.value)
      );
    },
    [facilityId, onFacilityUserSelectionChange]
  );

  useEffect(() => {
    setUsers(row.facilityUsers || []);

    const initialSelected = (row.facilityUsers || [])
      ?.filter((user: SignatureFacilityUser) => user.isSelected)
      .map((user: SignatureFacilityUser) => ({
        label: user.userName || "No Name",
        value: user.userId,
      }));
    setSelectedUsers(initialSelected);
    if (facilityId) {
      onFacilityUserSelectionChange(
        facilityId,
        initialSelected.map((user: Option) => user.value)
      );
    }
  }, [row.facilityUsers, facilityId, onFacilityUserSelectionChange]);

  return (
    <TableRow>
      <TableCell>{row.facilityName}</TableCell>
      <TableCell>
        {row.isSelectAllUser}
        <div className="form-check form-check-sm form-check-solid d-flex justify-content-center">
          <input
            id="allUsersCheckbox"
            className="form-check-input"
            type="checkbox"
            name="allUsers"
            checked={allSelected}
            onChange={handleSelectAllUsers}
          />
        </div>
      </TableCell>
      <TableCell>
        <MultiSelect
          styles={reactSelectStyle}
          theme={(theme) => styles(theme)}
          isMulti
          options={formattedUsers}
          name="users"
          placeholder={t("Select multi-users")}
          value={selectedUsers}
          menuPortalTarget={document.body}
          onChange={(selected) =>
            handleMultiSelectChange((selected as Option[] | null) ?? null)
          }
        />
      </TableCell>
    </TableRow>
  );
};

export default AssignedFacilityRow;
